/**
 * Lightweight word-level diff — used by the premium-rewrite Diff Modal to
 * highlight exactly which words the AI added (green) or removed (red).
 *
 * Implementation: classic LCS DP. Tokens are words + whitespace runs so
 * rejoining a segment array reproduces the source string verbatim (spaces,
 * newlines, punctuation adjacency all preserved).
 *
 * We short-circuit for O(N·M) safety: if the total token count exceeds
 * ~800 (both sides), we fall back to line-level diff. Slide content is
 * always well below this in practice, but the guard means a mis-configured
 * paste never lags the UI.
 */

/** Split into an array where every other token is whitespace. Both
 *  sequences share this tokenisation so LCS produces stable alignment. */
export function tokenize(str) {
  if (!str) return [];
  // Match either a run of non-whitespace OR a run of whitespace. `matchAll`
  // preserves both classes as separate tokens so rejoining reproduces the
  // exact original string.
  return Array.from(str.matchAll(/\S+|\s+/g), (m) => m[0]);
}

/** Compare two tokens for equality after normalising invisible whitespace
 *  variants — a single space vs multiple spaces are treated as the same
 *  "whitespace" token for diff-alignment purposes so trailing-space edits
 *  don't nuke the whole line. */
function eq(a, b) {
  if (a === b) return true;
  if (/^\s+$/.test(a) && /^\s+$/.test(b)) return true;
  return false;
}

/**
 * Compute a word-level diff. Returns an array of segments in output order:
 *   { type: 'equal' | 'add' | 'remove', text: string }
 * Adjacent segments of the same type are merged.
 */
export function diffWords(oldStr, newStr) {
  const a = tokenize(oldStr || "");
  const b = tokenize(newStr || "");
  const n = a.length, m = b.length;

  // Guardrail: fall back to line-level diff if the input is huge.
  if (n * m > 640000) return diffLines(oldStr, newStr);

  // DP table (Uint16 is enough for slide content lengths)
  const dp = new Array(n + 1);
  for (let i = 0; i <= n; i++) dp[i] = new Uint16Array(m + 1);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = eq(a[i], b[j])
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out = [];
  const push = (type, text) => {
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text;
    else out.push({ type, text });
  };

  let i = 0, j = 0;
  while (i < n && j < m) {
    if (eq(a[i], b[j])) {
      // Prefer NEW token text so highlighted whitespace matches the target
      // side (avoids showing collapsed spaces).
      push("equal", b[j]);
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push("remove", a[i]);
      i++;
    } else {
      push("add", b[j]);
      j++;
    }
  }
  while (i < n) { push("remove", a[i]); i++; }
  while (j < m) { push("add", b[j]); j++; }

  return out;
}

/** Slower fallback for huge inputs — line-level, guaranteed O(N+M). */
function diffLines(oldStr, newStr) {
  const a = (oldStr || "").split(/\n/);
  const b = (newStr || "").split(/\n/);
  const out = [];
  const push = (type, text) => {
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text;
    else out.push({ type, text });
  };
  const bSet = new Set(b);
  const aSet = new Set(a);
  for (const line of a) {
    if (bSet.has(line)) push("equal", line + "\n");
    else push("remove", line + "\n");
  }
  for (const line of b) {
    if (!aSet.has(line)) push("add", line + "\n");
  }
  return out;
}

/** Filter a diff to only the "old" side (removals + equals). */
export function diffToOldSegments(segments) {
  return segments.filter((s) => s.type !== "add");
}

/** Filter a diff to only the "new" side (additions + equals). */
export function diffToNewSegments(segments) {
  return segments.filter((s) => s.type !== "remove");
}
